import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Card, CardContent, Chip,
  CircularProgress, Divider, FormControl, InputLabel, MenuItem, Paper, Select,
  Stack, TextField, Typography,
} from '@mui/material';
import { Download as DownloadIcon, Terminal as TerminalIcon } from '@mui/icons-material';
import api from '../services/api';

/**
 * The installer library.
 *
 * Fill the form in, download a script that already carries the values, run it
 * on the target machine. The script is the SAME file that lives in the
 * repository -- only its `GENERATED CONFIGURATION` block is filled in, so a
 * diff against the repo copy shows that block and nothing else.
 *
 * The downloaded file contains whatever secret was typed into it (a Domain
 * Admin password, a single-use enrollment bundle), which is why the endpoint is
 * administrator-only and the response is never cached.
 */
// A value no real field can hold, standing in for the empty-string choice so
// MUI can render and select it. MUI treats '' as "nothing selected", which
// made a legitimate option ("Provision a NEW forest") look unset. Never leaves
// this file: it is translated back to '' before the value is submitted.
const EMPTY_CHOICE = '\u0000empty';

function Installers() {
  const [installers, setInstallers] = useState([]);
  const [selected, setSelected] = useState('');
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Enrollment, done HERE rather than on the Agents page.
  //
  // The old flow made you leave, create an agent, copy a bundle, come back and
  // paste it — and the field that needed it gave no hint where it came from.
  // The bundle is just (hub URL + one-time token + CA), all of which this page
  // can ask for, so it does.
  const [agents, setAgents] = useState([]);
  const [dcName, setDcName] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [enrollNote, setEnrollNote] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/installers/');
      const list = res.data.installers || [];
      setInstallers(list);
      if (list.length && !selected) setSelected(list[0].name);
      try {
        const a = await api.get('/agents/');
        setAgents(a.data.agents || a.data.results || a.data || []);
      } catch {
        // Not fatal: you can still type a new name and create it.
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the installer library.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const spec = installers.find((i) => i.name === selected);

  // Seed defaults whenever the chosen script changes, so a field with a
  // sensible default is not silently empty.
  //
  // Keyed on the SPEC, not on `selected`. The installer list loads
  // asynchronously, so `selected` is frequently set while `spec` is still
  // undefined; an effect keyed on `selected` alone then returned early and
  // never ran again, because `selected` never changed afterwards. The form
  // rendered with every field blank — including the ones that have defaults.
  const seededFor = useRef(null);
  useEffect(() => {
    if (!spec || seededFor.current === spec.name) return;
    seededFor.current = spec.name;
    const seed = {};
    spec.fields.forEach((f) => { seed[f.key] = f.default ?? ''; });
    setValues(seed);
  }, [spec]);

  const shown = (f) => {
    if (!f.depends_on) return true;
    return Object.entries(f.depends_on).every(([k, v]) => String(values[k] ?? '') === String(v));
  };

  /**
   * Give me an enrollment bundle for this DC.
   *
   * Creates the agent record if the name is new, re-issues if it already
   * exists (a re-install, or the first token expired), then fills in the hub
   * URL and the bundle. Re-issuing is safe: the token is single-use and the
   * old one stops working, which is what you want if it leaked.
   */
  const enroll = async () => {
    const name = dcName.trim();
    if (!name) { setError('Give the domain controller a name first.'); return; }
    setEnrolling(true); setError(''); setEnrollNote('');
    try {
      const known = agents.some((a) => a.name === name);
      const res = known
        ? await api.post(`/agents/${encodeURIComponent(name)}/enroll/`)
        : await api.post('/agents/create/', { name });
      const d = res.data || {};
      if (!d.bundle) {
        setError('The hub produced no bundle. Check AGENT_HUB_URL is set and https.');
        return;
      }
      setValues((v) => ({ ...v, GEN_BUNDLE: d.bundle, GEN_HUB: d.hub || v.GEN_HUB }));
      setFingerprint(d.fingerprint || '');
      setEnrollNote(known
        ? `Re-issued for "${name}". Any earlier token for it is now dead.`
        : `Created "${name}" and issued its enrollment token.`);
      if (!known) setAgents((prev) => [...prev, { name }]);
    } catch (e) {
      setError(e.response?.data?.detail || `Could not enroll "${name}".`);
    } finally {
      setEnrolling(false);
    }
  };

  const download = async () => {
    setBusy(true); setError('');
    try {
      const res = await api.post(`/installers/${spec.name}/render/`, values,
                                 { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/x-shellscript' }));
      const a = document.createElement('a');
      a.href = url; a.download = spec.filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      // A blob error body has to be read back as text before it means anything.
      let msg = 'Download failed.';
      try {
        const txt = await e.response?.data?.text?.();
        msg = JSON.parse(txt)?.detail || msg;
      } catch { msg = e.response?.status === 403
        ? 'Rendering an installer requires an administrator: the file carries the secrets you typed into it.'
        : msg; }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Installers</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Download a ready-to-run script with your values already in it.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <FormControl fullWidth sx={{ mb: 3, maxWidth: 520 }}>
        <InputLabel id="installer-label">Script</InputLabel>
        <Select labelId="installer-label" label="Script" value={selected}
                onChange={(e) => setSelected(e.target.value)}>
          {installers.map((i) => (
            <MenuItem key={i.name} value={i.name} disabled={!i.available}>
              {i.title}{!i.available && ' — not present in this deployment'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {spec && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <TerminalIcon color="action" />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>{spec.title}</Typography>
              {spec.platforms.map((p) => (
                <Chip key={p} size="small" variant="outlined" label={p} />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {spec.summary}
            </Typography>

            {spec.notes?.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Before you run it</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                  {spec.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </Alert>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* The agent script needs an enrollment bundle, which used to mean
                leaving for the Agents page. Do it here instead. */}
            {spec.name === 'agent' && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Step 1 — enroll the domain controller
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Name the DC you are about to install onto. This creates it here and
                  fills in the hub URL and its one-time enrollment bundle below. An
                  existing name re-issues the token, which is what you want for a
                  re-install.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                  <Autocomplete
                    freeSolo
                    options={agents.map((a) => a.name).filter(Boolean)}
                    value={dcName}
                    onInputChange={(e, v) => setDcName(v || '')}
                    sx={{ minWidth: 260, flexGrow: 1 }}
                    renderInput={(params) => (
                      <TextField {...params} size="small"
                                 label="Domain controller name"
                                 helperText="A new name creates it; an existing one re-issues." />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={enroll}
                    disabled={enrolling || !dcName.trim()}
                    startIcon={enrolling ? <CircularProgress size={16} /> : null}
                    sx={{ mt: 0.25 }}
                  >
                    {enrolling ? 'Enrolling…' : 'Enroll and fill in'}
                  </Button>
                </Stack>
                {enrollNote && (
                  <Alert severity="success" sx={{ mt: 1.5 }}>
                    {enrollNote}
                    {fingerprint && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption">
                          Hub CA fingerprint — the agent prints this on first start,
                          and it should match:
                        </Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem',
                                   wordBreak: 'break-all' }}>
                          {fingerprint}
                        </Box>
                      </Box>
                    )}
                  </Alert>
                )}
              </Paper>
            )}

            {spec.name === 'agent' && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Step 2 — check the values, then download
              </Typography>
            )}

            <Stack spacing={2}>
              {spec.fields.filter(shown).map((f) => (
                f.type === 'choice' ? (
                  <FormControl key={f.key} fullWidth>
                    <InputLabel id={`l-${f.key}`}>{f.label}</InputLabel>
                    {/* MUI treats an empty-string value as "nothing selected" and
                        renders the control blank, so a legitimate choice whose
                        value IS the empty string (here: "Provision a NEW forest")
                        looked unset and unselectable. Carry the empty choice
                        through the Select as a sentinel and translate it back on
                        the way out, so the generated script still receives "". */}
                    <Select labelId={`l-${f.key}`} label={f.label}
                            value={(values[f.key] ?? '') === '' ? EMPTY_CHOICE : values[f.key]}
                            onChange={(e) => setValues({
                              ...values,
                              [f.key]: e.target.value === EMPTY_CHOICE ? '' : e.target.value,
                            })}>
                      {f.choices.map((c) => (
                        <MenuItem key={String(c.value)}
                                  value={c.value === '' ? EMPTY_CHOICE : c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {f.help}
                    </Typography>
                  </FormControl>
                ) : (
                  <TextField
                    key={f.key} fullWidth label={f.label} helperText={f.help}
                    required={f.required}
                    type={f.secret && f.type !== 'textarea' ? 'password' : 'text'}
                    multiline={f.type === 'textarea'} minRows={f.type === 'textarea' ? 4 : undefined}
                    value={values[f.key] ?? ''}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  />
                )
              ))}
            </Stack>

            <Alert severity="warning" sx={{ mt: 2 }}>
              The downloaded file contains the values above <strong>in clear</strong>,
              including any password. Treat it as a secret: copy it to the target
              machine, run it, and delete it.
            </Alert>

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" startIcon={<DownloadIcon />}
                      onClick={download} disabled={busy || !spec.available}>
                {busy ? 'Preparing…' : `Download ${spec.filename}`}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mt: 2 }}>
              Step 3 — on the target machine:{' '}
              <code>chmod +x {spec.filename} &amp;&amp; sudo ./{spec.filename}</code>
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default Installers;
